import { createClient } from "@supabase/supabase-js";

// ── Paste your values from https://supabase.com/dashboard → Settings → API ──
const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL || "https://kftxqbaraolupywxfrrv.supabase.co";
const SUPABASE_KEY = process.env.REACT_APP_SUPABASE_KEY || "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImtmdHhxYmFyYW9sdXB5d3hmcnJ2Iiwicm9sZSI6ImFub24iLCJpYXQiOjE3NzM4OTc5NzMsImV4cCI6MjA4OTQ3Mzk3M30.KrwMDxx_9SUfqI9jsNdwnI_Oj5STWSJpow4KEUfo3vs";

export const supabase = createClient(SUPABASE_URL, SUPABASE_KEY);

// ─────────────────────────────────────────────────────────────────────────────
// HELPERS
// ─────────────────────────────────────────────────────────────────────────────
const handle = ({ data, error }) => {
  if (error) { console.error("Supabase:", error.message); throw error; }
  return data;
};

// Supabase uses snake_case columns; our React state uses camelCase.
// These two functions convert between them.
const toDb = (s, userId) => ({
  id:             String(s.id),
  user_id:        userId,
  name:           s.name            || "",
  phone:          s.phone           || "",
  email:          s.email           || "",
  plan:           s.plan            || "Weekly",
  type:           s.type            || "Veg",
  amount:         Number(s.amount)  || 0,
  status:         s.status          || "Pending",
  start_date:     s.startDate       || null,
  end_date:       s.endDate         || null,
  total_days:     Number(s.totalDays) || 7,
  excluded_days:  s.excludedDays    || [],
  meal_slots:     s.mealSlots       || ["Lunch"],
  slot_riders:    s.slotRiders      || {},
  pause_periods:  s.pausePeriods    || [],
  delivery_area:  s.deliveryArea    || "",
  location_mode:  s.locationMode    || "single",
  home_area:      s.homeArea        || "",
  office_area:    s.officeArea      || "",
  slot_type:      s.slotType        || {},
  slot_location:  s.slotLocation    || {},
  notes:          s.notes           || "",
  pause_token:    s.pauseToken      || null,
});

const fromDb = (row) => ({
  id:            row.id,
  userId:        row.user_id,
  name:          row.name,
  phone:         row.phone          || "",
  email:         row.email          || "",
  plan:          row.plan           || "Weekly",
  type:          row.type           || "Veg",
  amount:        Number(row.amount) || 0,
  status:        row.status         || "Pending",
  startDate:     row.start_date     || "",
  endDate:       row.end_date       || "",
  totalDays:     row.total_days     || 7,
  excludedDays:  row.excluded_days  || [],
  mealSlots:     row.meal_slots     || ["Lunch"],
  slotRiders:    row.slot_riders    || {},
  pausePeriods:  row.pause_periods  || [],
  deliveryArea:  row.delivery_area  || "",
  locationMode:  row.location_mode  || "single",
  homeArea:      row.home_area      || "",
  officeArea:    row.office_area    || "",
  slotType:      row.slot_type      || {},
  slotLocation:  row.slot_location  || {},
  notes:         row.notes          || "",
  pauseToken:    row.pause_token    || "",
  providerName:  row.provider_name  || "",
});

const userToDb = (u) => ({
  id:             u.id,
  login_id:       u.loginId,
  password:       u.password,
  name:           u.name,
  owner:          u.owner          || "",
  city:           u.city           || "",
  color:          u.color          || "#1D9E75",
  active:         u.active !== false,
  access_expiry:  u.accessExpiry   || null,
  logo_url:       u.logoUrl        || null,
});

const userFromDb = (row) => ({
  id:           row.id,
  loginId:      row.login_id,
  password:     row.password,
  name:         row.name,
  owner:        row.owner          || "",
  city:         row.city           || "",
  color:        row.color          || "#1D9E75",
  active:       row.active,
  accessExpiry: row.access_expiry  || "",
  logoUrl:      row.logo_url       || (row.menu_images && row.menu_images[0]) || "",
  menuImages:   row.menu_images    || [],
});

// ─────────────────────────────────────────────────────────────────────────────
// AUTH / USERS
// ─────────────────────────────────────────────────────────────────────────────
export const dbLogin = async (loginId, password) => {
  // Use .eq filter and get array — .single() causes 406 if RLS blocks or no row found
  const { data, error } = await supabase
    .from("mealbox_users")
    .select("*")
    .eq("login_id", loginId.trim())
    .limit(1);

  if (error) {
    console.error("Login query error:", error.message, error.code);
    return { success: false, error: "Connection error. Check your Supabase URL and key." };
  }
  const found = (data || []).find(u => u.login_id === loginId.trim());
  if (!found) return { success: false, error: "Invalid login ID or password" };
  if (found.password !== password) return { success: false, error: "Invalid login ID or password" };
  if (!found.active) return { success: false, error: "Account suspended. Contact admin." };
  if (found.access_expiry && new Date(found.access_expiry) < new Date())
    return { success: false, error: `Access expired on ${found.access_expiry}. Contact admin.` };
  return { success: true, user: userFromDb(found) };
};

export const dbFetchUsers = async () => {
  const { data, error } = await supabase.from("mealbox_users").select("*").order("id");
  if (error) { console.error("dbFetchUsers:", error.message); return []; }
  return (data || []).map(userFromDb);
};

export const dbSaveUser = async (user) => {
  const { data, error } = await supabase.from("mealbox_users").upsert(userToDb(user)).select().single();
  if (error) { console.error("dbSaveUser:", error.message); throw error; }
  return userFromDb(data);
};

export const dbDeleteUser = async (id) => {
  const { error } = await supabase.from("mealbox_users").delete().eq("id", id);
  if (error) { console.error("dbDeleteUser:", error.message); throw error; }
};

// ─────────────────────────────────────────────────────────────────────────────
// SUBSCRIBERS
// ─────────────────────────────────────────────────────────────────────────────
export const dbFetchSubscribers = async (userId) => {
  const rows = handle(await supabase.from("mealbox_subscribers").select("*").eq("user_id", userId).order("name"));
  return rows.map(fromDb);
};

export const dbSaveSubscriber = async (subscriber, userId) => {
  const row = handle(await supabase.from("mealbox_subscribers").upsert(toDb(subscriber, userId)).select().single());
  return fromDb(row);
};

export const dbDeleteSubscriber = async (id) =>
  handle(await supabase.from("mealbox_subscribers").delete().eq("id", id));

// Fetch a subscriber by their self-service pause token (used by SubscriberPortal)
export const dbFetchSubscriberByToken = async (token) => {
  const { data, error } = await supabase
    .from("mealbox_subscribers")
    .select("*")
    .eq("pause_token", token)
    .limit(1);
  if (error) { console.error("dbFetchSubscriberByToken:", error.message); return null; }
  const row = (data || [])[0];
  return row ? fromDb(row) : null;
};

// Update ONLY pause-related fields via pause_token — no auth/user_id required.
// Requires this RLS policy in Supabase:
//   CREATE POLICY "portal_pause_update" ON mealbox_subscribers
//   FOR UPDATE USING (true) WITH CHECK (true);
// (or scope it to anon role if preferred)
export const dbSavePortalPause = async (token, pausePeriods, status, endDate) => {
  const { data, error } = await supabase
    .from("mealbox_subscribers")
    .update({
      pause_periods: pausePeriods,
      status,
      end_date: endDate || null,
    })
    .eq("pause_token", token)
    .select()
    .single();
  if (error) { console.error("dbSavePortalPause:", error.message); throw new Error(error.message); }
  return fromDb(data);
};

// Fetch just the branding fields for a provider (used by SubscriberPortal header)
export const dbFetchProviderBrand = async (userId) => {
  const { data, error } = await supabase
    .from("mealbox_users")
    .select("name, logo_url, color, menu_images")
    .eq("id", userId)
    .limit(1);
  if (error) { console.error("dbFetchProviderBrand:", error.message); return null; }
  const row = (data || [])[0];
  if (!row) return null;
  // Resolve logo: prefer explicit logo_url, fall back to first menu image
  const logoUrl = row.logo_url || (Array.isArray(row.menu_images) && row.menu_images[0]) || "";
  return { name: row.name || "", logoUrl, color: row.color || "#1D9E75" };
};

// ─────────────────────────────────────────────────────────────────────────────
// AREAS
// ─────────────────────────────────────────────────────────────────────────────
export const dbFetchAreas = async (userId) => {
  const rows = handle(await supabase.from("mealbox_areas").select("name").eq("user_id", userId).order("position"));
  return rows.map(r => r.name);
};

export const dbSaveAreas = async (userId, areaNames) => {
  await supabase.from("mealbox_areas").delete().eq("user_id", userId);
  if (areaNames.length === 0) return;
  const rows = areaNames.map((name, i) => ({ user_id: userId, name, position: i }));
  handle(await supabase.from("mealbox_areas").insert(rows));
};

// ─────────────────────────────────────────────────────────────────────────────
// RIDERS
// ─────────────────────────────────────────────────────────────────────────────
export const dbFetchRiders = async (userId) => {
  const rows = handle(await supabase.from("mealbox_riders").select("name").eq("user_id", userId).order("position"));
  return rows.map(r => r.name);
};

export const dbSaveRiders = async (userId, riderNames) => {
  await supabase.from("mealbox_riders").delete().eq("user_id", userId);
  if (riderNames.length === 0) return;
  const rows = riderNames.map((name, i) => ({ user_id: userId, name, position: i }));
  handle(await supabase.from("mealbox_riders").insert(rows));
};

// ─────────────────────────────────────────────────────────────────────────────
// ACTIVITY LOG
// ─────────────────────────────────────────────────────────────────────────────
export const dbLogActivity = async (userId, icon, color, text) =>
  supabase.from("mealbox_activity").insert({ user_id: userId, icon, color, text, time: new Date().toISOString() });

export const dbFetchActivity = async (userId) => {
  const rows = handle(await supabase.from("mealbox_activity").select("*").eq("user_id", userId).order("time", { ascending: false }).limit(20));
  return rows.map(r => ({ icon: r.icon, color: r.color, text: r.text, time: r.time }));
};

// ─────────────────────────────────────────────────────────────────────────────
// SESSION LOGS
// ─────────────────────────────────────────────────────────────────────────────
export const dbLogSession = async (user, event) => {
  const device = navigator.userAgent.substring(0, 120);
  return supabase.from("mealbox_session_logs").insert({
    user_id:   user.id,
    user_name: user.name,
    event,
    device,
    time: new Date().toISOString(),
  });
};

export const dbFetchSessionLogs = async (limit = 100) => {
  const { data, error } = await supabase
    .from("mealbox_session_logs")
    .select("*")
    .order("time", { ascending: false })
    .limit(limit);
  if (error) { console.error("Session logs error:", error.message); return []; }
  return data || [];
};

// ─────────────────────────────────────────────────────────────────────────────
// KEEPALIVE — called on login to prevent Supabase free tier pausing
// ─────────────────────────────────────────────────────────────────────────────
export const dbKeepAlive = () => {
  // Silently ping the DB — no await, no blocking, fire and forget
  supabase
    .from("mealbox_users")
    .select("id")
    .limit(1)
    .then(() => {})
    .catch(() => {});
};

// ─────────────────────────────────────────────────────────────────────────────
// LEADS
// ─────────────────────────────────────────────────────────────────────────────
const leadToDb = (l, userId) => ({
  id:            String(l.id),
  user_id:       userId,
  name:          l.name          || "",
  phone:         l.phone         || "",
  email:         l.email         || "",
  source:        l.source        || "Manual",
  status:        l.status        || "New",
  meal_interest: l.mealInterest  || "",
  area:          l.area          || "",
  notes:         l.notes         || "",
  follow_up:     l.followUp      || null,
  converted_id:  l.convertedId   || null,
  drop_reason:        l.dropReason       || null,
  dropped_from_stage: l.droppedFromStage || null,
  dropped_at:         l.droppedAt        || null,
});

const leadFromDb = (row) => ({
  id:           row.id,
  name:         row.name,
  phone:        row.phone         || "",
  email:        row.email         || "",
  source:       row.source        || "Manual",
  status:       row.status        || "New",
  mealInterest: row.meal_interest || "",
  area:         row.area          || "",
  notes:        row.notes         || "",
  followUp:     row.follow_up     || "",
  convertedId:  row.converted_id  || null,
  dropReason:       row.drop_reason        || "",
  droppedFromStage: row.dropped_from_stage || null,
  droppedAt:        row.dropped_at         || null,
  createdAt:    row.created_at    || "",
});

export const dbFetchLeads = async (userId) => {
  const { data, error } = await supabase
    .from("mealbox_leads")
    .select("*")
    .eq("user_id", userId)
    .order("created_at", { ascending: false });
  if (error) { console.error("dbFetchLeads:", error.message); return []; }
  return (data || []).map(leadFromDb);
};

export const dbSaveLead = async (lead, userId) => {
  const { data, error } = await supabase
    .from("mealbox_leads")
    .upsert(leadToDb(lead, userId))
    .select()
    .single();
  if (error) { console.error("dbSaveLead:", error.message); throw error; }
  return leadFromDb(data);
};

export const dbDeleteLead = async (id) => {
  const { error } = await supabase.from("mealbox_leads").delete().eq("id", id);
  if (error) { console.error("dbDeleteLead:", error.message); throw error; }
};

// ─────────────────────────────────────────────────────────────────────────────
// MARKETPLACE APPROVAL REQUESTS
// ─────────────────────────────────────────────────────────────────────────────

/** Provider submits a change request */
export const dbSubmitMarketplaceRequest = async (userId, providerName, payload) => {
  // Cancel any existing pending request first (only one pending at a time)
  await supabase
    .from("mealbox_marketplace_requests")
    .update({ status: "cancelled" })
    .eq("user_id", userId)
    .eq("status", "pending");

  const { data, error } = await supabase
    .from("mealbox_marketplace_requests")
    .insert({ user_id: userId, provider_name: providerName, payload, status: "pending" })
    .select()
    .single();
  if (error) { console.error("dbSubmitMarketplaceRequest:", error.message); throw error; }
  return data;
};

/** Check if provider has a pending request */
export const dbFetchMyRequest = async (userId) => {
  const { data, error } = await supabase
    .from("mealbox_marketplace_requests")
    .select("*")
    .eq("user_id", userId)
    .eq("status", "pending")
    .order("submitted_at", { ascending: false })
    .limit(1);
  if (error) { console.error("dbFetchMyRequest:", error.message); return null; }
  return (data || [])[0] || null;
};

/** Admin: fetch all requests */
export const dbFetchAllRequests = async () => {
  const { data, error } = await supabase
    .from("mealbox_marketplace_requests")
    .select("*")
    .in("status", ["pending", "approved", "rejected"])
    .order("submitted_at", { ascending: false })
    .limit(100);
  if (error) { console.error("dbFetchAllRequests:", error.message); return []; }
  return data || [];
};

/** Admin: approve a request — applies payload to mealbox_users */
export const dbApproveRequest = async (requestId, userId, payload) => {
  // Explicitly pick only the columns we know exist — avoids schema cache errors
  const update = {};
  const fields = [
    "name","tagline","description","whatsapp","city",
    "is_listed","diet_types","meal_types","plans",
    "areas_served","menu_images","logo_color","color",
    "rating","review_count",
  ];
  fields.forEach(f => { if (payload[f] !== undefined) update[f] = payload[f]; });

  const { error: applyErr } = await supabase
    .from("mealbox_users")
    .update(update)
    .eq("id", userId);
  if (applyErr) throw applyErr;

  const { error } = await supabase
    .from("mealbox_marketplace_requests")
    .update({ status: "approved", reviewed_at: new Date().toISOString() })
    .eq("id", requestId);
  if (error) throw error;
};

/** Admin: reject a request with a note */
export const dbRejectRequest = async (requestId, note) => {
  const { error } = await supabase
    .from("mealbox_marketplace_requests")
    .update({ status: "rejected", note: note || "", reviewed_at: new Date().toISOString() })
    .eq("id", requestId);
  if (error) throw error;
};

// ─────────────────────────────────────────────────────────────────────────────
// FORM SUBMISSIONS (List Your Business)
// ─────────────────────────────────────────────────────────────────────────────
export const dbSubmitBusinessForm = async (data) => {
  const { error } = await supabase.from("mealbox_business_forms").insert({
    business_name: data.businessName || "",
    whatsapp:      data.whatsapp     || "",
    phone:         data.phone        || "",
    city:          data.city         || "",
    submitted_at:  new Date().toISOString(),
    status:        "new",
  });
  if (error) throw error;
};

export const dbFetchBusinessForms = async () => {
  const { data, error } = await supabase
    .from("mealbox_business_forms")
    .select("*")
    .order("submitted_at", { ascending: false });
  if (error) { console.error("dbFetchBusinessForms:", error.message); return []; }
  return data || [];
};

export const dbUpdateEnquiryStatus = async (id, status) => {
  const { error } = await supabase
    .from("mealbox_business_forms")
    .update({ status })
    .eq("id", id);
  if (error) throw error;
};

export const dbFetchWaLeads = async () => {
  const { data, error } = await supabase
    .from("mealbox_wa_leads")
    .select("*")
    .order("clicked_at", { ascending: false });
  if (error) { console.error("dbFetchWaLeads:", error.message); return []; }
  return data || [];
};

export const dbUpdateWaLeadStatus = async (id, status) => {
  const { error } = await supabase
    .from("mealbox_wa_leads")
    .update({ status })
    .eq("id", id);
  if (error) throw error;
  return true;
};
